/**
 * Our own OI declarations from FlightBlender (ENG-313) — the schedule-based OI
 * source. `GET /flight_declaration_ops/flight_declaration` through the same
 * `/oi-api` proxy as the DSS search.
 *
 * Verified live 2026-10-19 (airmarket): the list is paginated, honours
 * `start_date` / `end_date` / `page_size` and IGNORES `state` / `is_approved`,
 * so those are filtered here. It also holds declarations from other parties and
 * a long tail of Rejected / unapproved ones — only approved, Accepted-to-
 * Contingent declarations from our own party become OIs.
 */
import { config } from '@/lib/config';
import { rtmStatusFromCode } from '@/lib/oi/rtmStatus';
import { firstPolygon, validIso } from './oiSearch';
import type { OiSummary } from './types';

export type FlightDeclaration = {
  id?: string;
  state?: number;
  is_approved?: boolean;
  originating_party?: string | null;
  plan_id?: string | null;
  start_datetime?: string;
  end_datetime?: string;
  flight_declaration_geojson?: { features?: { geometry?: unknown }[] } | null;
};

type DeclarationPage = {
  results?: FlightDeclaration[];
};

/** RTM states that mean "this OI is live in the airspace": Accepted, Active,
 *  Non-Conforming, Contingent. Ended (5) and Rejected (8) are not OIs. */
const LIVE_STATES = new Set([1, 2, 3, 4]);

const MAX_PAGES = 4;
const PAGE_SIZE = 500;

/** `YYYY-MM-DD` — the only date format verified against the endpoint. */
function isoDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** One declaration → an OI, or null when it isn't one of ours to show:
 *  unapproved, not in a live state, another party's, or without a polygon / id /
 *  parseable window. Aircraft-less by design — Blender doesn't say which drone
 *  flies it until take-off. */
export function toBlenderOi(d: FlightDeclaration, ownParty: string): OiSummary | null {
  if (!d.id || d.is_approved !== true || !LIVE_STATES.has(d.state ?? -1)) return null;
  if (ownParty && (d.originating_party ?? '').toLowerCase() !== ownParty.toLowerCase()) {
    return null;
  }
  const polygon = firstPolygon(d.flight_declaration_geojson?.features?.[0]?.geometry);
  const start = validIso(d.start_datetime);
  const end = validIso(d.end_datetime);
  if (!polygon || !start || !end) return null;
  return {
    oi_id: d.id,
    title: d.plan_id ?? '',
    status: rtmStatusFromCode(d.state),
    aircraft: '',
    drone_model: '—',
    start_utc: start,
    end_utc: end,
    pilot_name: '—',
    ops_approval: 'Approved',
    op_type: '—',
    legal_rule: '—',
    submission_date: null,
    polygon,
    subscriber_count: 0,
    last_updated_utc: start,
    origin: 'internal',
    partner_tenant: null,
  };
}

/** Our approved, live-state declarations around now: last 24 h through the
 *  next day, so an OI whose window opens between polls is already in hand. */
export async function fetchOwnOiDeclarations(nowMs: number): Promise<OiSummary[]> {
  const out: OiSummary[] = [];
  for (let page = 1; page <= MAX_PAGES; page++) {
    const params = new URLSearchParams({
      start_date: isoDate(nowMs - 86_400_000),
      end_date: isoDate(nowMs + 2 * 86_400_000),
      page_size: String(PAGE_SIZE),
      page: String(page),
    });
    const res = await fetch(
      `${config.oiApi.base}/flight_declaration_ops/flight_declaration?${params.toString()}`,
      { headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(20_000) },
    );
    // A page past the end is a 404, not a failure.
    if (res.status === 404 && page > 1) break;
    if (!res.ok) throw new Error(`OI declarations error ${res.status}`);
    const body = (await res.json()) as DeclarationPage;
    const results = body.results ?? [];
    for (const d of results) {
      const oi = toBlenderOi(d, config.oiApi.ownParty);
      if (oi) out.push(oi);
    }
    // `pages` is worked out from the default page size (10), so it overstates
    // the count when `page_size` is raised — a short page is the last page.
    if (results.length < PAGE_SIZE) break;
  }
  return out;
}
